<template>
    <div class="sponsored-products">
        <div v-if="!sponsoredPending">
            <ais-instant-search :search-client="searchClient" :index-name="useReactiveSearchCreds().reactiveBaseAppName" :key="webcodes.join('|')"
            :future="{ preserveSharedStateOnUnmount: true }">
                <ais-configure
                    :hits-per-page.camel="15"
                    :filters="searchFilter" />
                <ais-hits :transform-items="mapData">
                    <template v-slot="{ items }">
                        <slot
                            name="render"
                            :error="error"
                            :gotSponsored="gotSponsored"
                            :sponsoredPending="sponsoredPending"
                            :beaconMap="beaconMap"
                            :sponsoredData="items"
                            :onLoadBeacon="onLoadBeacon"
                            :onViewBeacon="onViewBeacon" />
                    </template>
                </ais-hits>
            </ais-instant-search>
        </div>
    </div>
</template>
<script setup lang="ts">
import {
    AisInstantSearch,
    AisConfigure,
    AisHits,
} from 'vue-instantsearch/vue3/es';
import { ProvideNames } from '#shared/statics';
import { getSponsoredProducts } from "~/composables/ExpApi/expSponsoredProducts";
import type { AisArticle } from "@expert/types";
import type { SponsoredArticleHit } from "~~/models/AisSearch/Result";
interface WebCodesAndBeacons {
  webCodesArray: [];
  beaconMap: Map;
}

const props = defineProps({
    currentCategory: {
        type: Object || undefined,
    },
    categoryList: {
        type: Array,
    },
    pageType: {
        type: String
    },
    storeId: {
        type: String
    },
    brands: {
        type: Array,
        default: [],
    },
    articleSKUs: {
        type: Array,
    }
});

const error = ref();
const gotSponsored = ref(false);
const sponsoredPending = ref(true);
const beaconMap = ref(new Map());
const onLoadBeacon = ref();
const onViewBeacon = ref();

const webcodes = ref([]);
const searchFilter = ref('');

const optionals = computed(() => {
  let articleSKUs = props?.articleSKUs;
  let categoryId = undefined;
  let searchWord = undefined;

  if (props?.pageType === 'CATEGORY_PAGE') {
    categoryId = props?.categoryList ? props?.categoryList?.map(cc => cc.extId || cc.categoryId) : [props?.currentCategory?.categoryId]
  } else if (props?.pageType === 'SEARCH_PAGE') {
    searchWord = useSearchWord().value || useRoute()?.query?.q?.toString()
  }

  if (!articleSKUs) {
    articleSKUs = gtmDataContainer().value?.ecommerce?.impressions?.map(impression => impression?.id);
  }

  return {
    articleSKUs: articleSKUs.filter(Boolean),
    categoryId,
    searchWord,
    brands: props?.brands,
  }
});

const baseDir: string = inject(ProvideNames.BASE_DIR) ?? '';
const searchClient = useNuxtApp().$searchClient("/api/search/ppm-category")

const getWebCodesAndBeaconsFromSponsoredProducts = (sponsoredProducts): WebCodesAndBeacons => {
  const webCodesArray = [];
  const beaconMap = new Map();
  for (let sponsoredProduct of sponsoredProducts) {
    webCodesArray.push(sponsoredProduct.articleReference);
    beaconMap.set(sponsoredProduct.articleReference, sponsoredProduct);
  }
  return {
    webCodesArray,
    beaconMap
  };
}

const setFilter = async () => {
  const userState = useCurrentUserState();
  const sponsoredProductsResponse = await getSponsoredProducts(userState.value, props?.pageType ?? 'CATEGORY_PAGE', optionals.value);
  sponsoredPending.value = sponsoredProductsResponse?.pending?.value;
  const sponsored = toRaw(sponsoredProductsResponse?.data?.value);
  error.value = sponsoredProductsResponse?.error?.value;

  if (sponsored) {
    console.info("[bt-sponsoredProductList] response: ", sponsored);

    const webCodesAndBeacons: WebCodesAndBeacons = getWebCodesAndBeaconsFromSponsoredProducts(sponsored?.articles ?? []);
    const availableCount = webCodesAndBeacons.webCodesArray?.length ?? 0;
    
    beaconMap.value = webCodesAndBeacons.beaconMap;
    webcodes.value = webCodesAndBeacons.webCodesArray;
    onLoadBeacon.value = sponsored?.onLoadBeacon;
    onViewBeacon.value = sponsored?.onViewBeacon;
    gotSponsored.value = sponsored?.articles?.length > 0;
    if(import.meta.client){
      $fetch('/api/sponsoring-service/sponsored-available', {
                        method: 'POST',
                        body: {
                        pageType: props?.pageType,
                        device: useDevice().isMobile ? 'mobile' : 'desktop',
                        count: availableCount || 0,
                        type: 'available'
                        }
                    });
    }
    
    searchFilter.value = (webcodes?.value?.length > 1 ? 'articleNumber:(' + webcodes?.value?.join(' OR ') + ')' : 'articleNumber:' + webcodes?.value?.at(0));

  } else if (error.value) {
    console.error('[bt-sponsoredProductList] error response: ', sponsoredProductsResponse);
    searchFilter.value = ('articleNumber:1');
  } else {
    console.info("[bt-sponsoredProductList] empty response: ", sponsored);
    searchFilter.value = ('articleNumber:1');
  }
}

await setFilter();

const reloadSponsoredProducts = async () => {
  await setFilter();
}

watch(() => props?.brands, (newBrands, oldBrands) => {
  /*TODO: Used To be LoDash isEqual for deep Comparison.
    Quickfixed after removing lodash plugin. 
    Better implement some form of deepQuals again.
  */
  if (toRaw(newBrands) !== toRaw(oldBrands)) {
    reloadSponsoredProducts();
  }
});

const mapData = (data: Array<AisArticle>): Array<SponsoredArticleHit> => {
  const resArray: Array<SponsoredArticleHit> = [];
  data?.forEach(
    (res: AisArticle) => {
      if(res?.articleView?.contextSensitiveData != null && res?.storeData != null) {
        const newEntry = {
        ...res,
        beacons: beaconMap?.value?.get(res.articleView.articleNumber),
        };
        resArray.push(newEntry);
      }
    }
  );
  return resArray;
}
</script>